import dynamic from "next/dynamic";
import React, { Fragment, useEffect, useState } from "react";
import Image from "next/image";
import { Tooltip } from "react-tooltip";
import { toast } from "react-toastify";
import { generateJWTToken } from "@/utils/helpers/helperFunctions";

const Icon = dynamic(() => import("@/components/ui/Icon"), { ssr: false });

// Constants
const CONTRACT_ADDRESS = "0x0A10D108E2d81ccC793E37b56206C84bF96ddc57";
const TOAST_CONFIG = {
  position: "top-right",
  autoClose: 2000,
  hideProgressBar: false,
  closeOnClick: true,
  pauseOnHover: true,
  draggable: true,
  progress: undefined,
};

const TickerData = () => {
  const [tickerData, setTickerData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isSliding, setIsSliding] = useState(false);

  // Define exchange icons once
  const exchangeIcons = [
    {
      src: "https://res.cloudinary.com/dot2onqdb/image/upload/v1727849175/gate_icon_lqcgxi.png",
      alt: "Gate",
      link: "https://www.gate.io/trade/XSEED_USDT",
    },
    // { src: "https://res.cloudinary.com/dynb4avhf/image/upload/v1732701482/mexc_logo_kcmwnj.jpg", alt: "Mexc", link: "https://www.mexc.com/exchange/XSEED_USDT" },
    {
      src: "https://res.cloudinary.com/dot2onqdb/image/upload/v1728974483/lfj_zlggae.png",
      alt: "LFJ",
      link: "https://lfj.gg/avalanche/trade/?inputCurrency=AVAX&outputCurrency=0x0A10D108E2d81ccC793E37b56206C84bF96ddc57",
    },
    {
      src: "https://res.cloudinary.com/dynb4avhf/image/upload/v1751952761/uniswap-uni-logo_autga4.png",
      alt: "Uniswap",
      link: "https://app.uniswap.org/swap?chain=avalanche&inputCurrency=NATIVE&outputCurrency=0x0A10D108E2d81ccC793E37b56206C84bF96ddc57",
    },
    // { src: "https://res.cloudinary.com/dynb4avhf/image/upload/v1732700874/coingecko_logo_xieevg.png", alt: "Coingecko", link: "https://www.coingecko.com/en/coins/mxs-games" },
    // { src: "https://res.cloudinary.com/du44pwjoi/image/upload/v1731348963/dextools1629975030345_crcikc.png", alt: "Dextools", link: "https://www.dextools.io/app/en/avalanche/pair-explorer/0x461eff298ce37fb0a09c9ac15688f0913c97340c?t=1731346782850" },
    {
      src: "https://res.cloudinary.com/du44pwjoi/image/upload/v1737555298/coinmarketcap_black_logomark_x97esp.png",
      alt: "Coinmarketcap",
      link: "https://coinmarketcap.com/currencies/mxs-games/",
    },
  ];

  const handleCopyAddress = () => {
    navigator.clipboard.writeText(CONTRACT_ADDRESS).then(() => {
      toast.success("Copied to clipboard!", TOAST_CONFIG);
    });
  };

  useEffect(() => {
    const fetchTickerData = async () => {
      setLoading(true);
      try {
        const token = generateJWTToken();
        const response = await fetch(
          "https://cqfjxolodeqrlvaifybm.supabase.co/functions/v1/get-ticker-data",
          {
            method: "GET",
            headers: {
              Authorization: `Bearer ${token}`,
              "Content-Type": "application/json",
            },
          },
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const responseData = await response.json();
        const data = responseData.data || [];

        if (
          Array.isArray(data) &&
          data.every(
            (item) =>
              item.icon && item.name && item.button_link && item.button_label,
          )
        ) {
          setTickerData(data);
        } else {
          console.error("Invalid ticker data format:", responseData);
          setTickerData([]);
        }
      } catch (error) {
        console.error("Error fetching ticker data:", error);
        setTickerData([]);
        toast.error("Failed to load ticker data!", TOAST_CONFIG);
      } finally {
        setLoading(false);
      }
    };

    fetchTickerData();
  }, []);

  // console.log(tickerData);

  useEffect(() => {
    const interval = setInterval(() => {
      handleNextSlide();
    }, 5000);

    return () => clearInterval(interval);
  }, [tickerData]);

  const handleNextSlide = () => {
    setIsSliding(true);
    setTimeout(() => {
      setCurrentIndex((prevIndex) =>
        prevIndex === tickerData.length - 1 ? 0 : prevIndex + 1,
      );
      setIsSliding(false);
    }, 500);
  };

  if (loading || tickerData.length === 0) return null;

  const { icon, name, button_link, button_label } = tickerData[currentIndex];

  return (
    <Fragment>
      <div className="hidden lg:block">
        <div className="w-full flex flex-col items-center gap-10 sm:flex-row sm:justify-between sm:items-center ml-16">
          <div className="w-[400px] rounded-lg">
            <div className="relative text-metaxseed-light-green flex flex-col overflow-hidden my-4 w-full rounded-md">
              <div className="border-2 border-metaxseed-gray py-1 px-6 overflow-hidden rounded-md transition-colors duration-200">
                <div
                  className={`flex items-center justify-between w-full space-x-6 transition-transform duration-500 ease-out ${
                    isSliding ? "-translate-x-full" : "translate-x-1"
                  }`}
                >
                  <div className="flex gap-3 items-center min-w-0">
                    <Icon
                      icon={icon}
                      className="w-4 h-4 rounded-full"
                      alt={`${name} icon`}
                    />
                    <span className="text-[12px] gotham truncate">{name}</span>
                  </div>

                  <a
                    href={button_link}
                    className="hover:jello-horizontal bg-gradient-to-r from-metaxseed-orange to-yellow-500 py-1 px-3 rounded-md cursor-pointer text-[12px] gotham flex items-center whitespace-nowrap"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {button_label} →
                  </a>
                </div>
              </div>

              {/* Slider Dots positioned at the bottom */}
              <div className="flex justify-center space-x-1 mt-2">
                {tickerData.map((_, index) => (
                  <button
                    key={index}
                    className={`${
                      index === currentIndex
                        ? "w-2 h-1 rounded-lg bg-white/50"
                        : "w-1 h-1 rounded-full bg-gray-400/50"
                    }`}
                    onClick={() => {
                      setIsSliding(true);
                      setTimeout(() => {
                        setCurrentIndex(index);
                        setIsSliding(false);
                      }, 500);
                    }}
                    aria-label={`Slide ${index + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          {/* GET METAXSEED Token Section */}
          <div className="mr-10 flex flex-col sm:flex-row justify-around items-center gap-4 w-full sm:w-auto mb-2">
            <p
              className="text-metaxseed-light-green text-[13px] gotham flex items-center cursor-pointer"
              data-tooltip-id="token"
              onClick={handleCopyAddress}
            >
              Get $XSEED:
            </p>
            <div className="flex gap-2">
              {exchangeIcons.map(({ src, alt, link }, index) => (
                <div
                  key={index}
                  className="w-8 h-8 border-2 border-metaxseed-gray rounded-full flex items-center justify-center cursor-pointer bg-white"
                  onClick={() => window.open(link, "_blank")}
                >
                  <Image
                    src={src}
                    width={20}
                    height={20}
                    className="w-6 h-6 rounded-full"
                    alt={alt}
                  />
                </div>
              ))}
            </div>

            <button
              className="hover:jello-horizontal bg-transparent border-2 border-metaxseed-gray text-metaxseed-light-green px-4 py-2 hover:bg-white hover:text-gray-900 transition-colors duration-300 text-[12px] gotham rounded-md"
              onClick={() =>
                window.open(
                  "https://www.team.finance/view-coin/0x0A10D108E2d81ccC793E37b56206C84bF96ddc57?name=MXS%20Games&symbol=XSEED&chainid=0xa86a",
                  "_blank",
                )
              }
            >
              Stake & Earn
            </button>
            <Tooltip
              id="coming-soon"
              content="Coming soon"
              place="bottom"
              className="!rounded-full px-4 !bg-metaxseed-content-gray text-white z-50 !text-[10px]"
            />
            <Tooltip
              id="token"
              content={CONTRACT_ADDRESS}
              place="bottom"
              className="!rounded-full px-4 !bg-metaxseed-content-gray text-white z-50 !text-[14px]"
            />
          </div>
        </div>
      </div>

      {/* Mobile View - Responsive */}
      <div className="lg:hidden">
        <div className="w-full flex flex-col items-center px-2 sm:px-4 my-2 sm:my-4">
          {/* Mobile Get XSEED section */}
          <div className="w-full max-w-md mx-auto">
            {/* Icons - always visible */}
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3 px-2 sm:px-4">
              {exchangeIcons.map(({ src, alt, link }, index) => (
                <div
                  key={index}
                  className="w-7 h-7 sm:w-8 sm:h-8 border-2 border-metaxseed-gray rounded-full flex items-center justify-center cursor-pointer bg-white hover:scale-105 transition-transform"
                  onClick={() => window.open(link, "_blank")}
                >
                  <Image
                    src={src}
                    width={20}
                    height={20}
                    className="w-5 h-5 sm:w-6 sm:h-6 rounded-full"
                    alt={alt}
                  />
                </div>
              ))}
            </div>

            <Tooltip
              id="token-mobile"
              content={CONTRACT_ADDRESS}
              place="bottom"
              className="!rounded-full px-3 sm:px-4 !bg-metaxseed-content-gray text-white z-50 !text-xs sm:!text-[14px]"
            />
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export default TickerData;
